/**
 * STOCK HISTÓRICO, KÁRDEX Y CIERRES DE INVENTARIO (F13.6)
 *
 *   /inventario/stock?fecha=                            F13.7: el stock a un día pasado es la pestaña Stock con fecha
 *   /inventario/stock-historico                         (redirige a la de arriba: se conserva por los enlaces guardados)
 *   /inventario/kardex?idArticulo=&idAlmacen=&…         movimientos en orden con saldo acumulado
 *   /inventario/cierres                                 cierres mensuales (versión, estado, incidencias, conciliación)
 *   /inventario/cierres/:id                             ficha: KPI por unidad, líneas, incidencias, regenerar, conciliar, exportar
 *   /inventario/cierres/:id/conciliaciones/:numero      resultado de una conciliación con el ERP
 *
 * STOCK = SUMA DEL LEDGER: todo lo calcula el servidor con un único motor. Un cierre es una foto informativa: no
 * bloquea nada ni mueve stock; si algo la altera se avisa y se regenera (versión nueva). La conciliación compara con
 * lo que declara el ERP y no ajusta nada. Para administración: códigos de negocio y nombres, nunca UUID ni tokens.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  api, type CierreDetalle, type CierreResumen, type Conciliacion, type EstadoConciliacion, type Kardex, type LineaCierre, type OpcionAlmacenStock,
  type OpcionArticuloStock, type OpcionesHistorico, type OpcionLote, type StockHistorico,
} from '../api.ts';
import { Error as ErrorCaja, fecha, fechaHora, hoyLocal, Modal, Panel, useDatos, Vacio } from '../componentes/base.tsx';
import { Buscador } from '../componentes/buscador.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useSesion } from '../contexto.tsx';
import { guardarFichero } from './exportaciones.tsx';
import { buscarAlmacenes, buscarArticulos, erroresDe, nombreAlmacen, nombreArticulo } from './stock.tsx';
import { buscarLotes } from './trazabilidad.tsx';

const H = '/api/inventario/historico';
const C = '/api/inventario/cierres';

/** Cantidad en castellano (coma decimal). */
const n = (v: string | null | undefined): string => (v === null || v === undefined ? '' : v.replace('.', ','));
const plural = (k: number, uno: string, varios: string): string => `${String(k)} ${k === 1 ? uno : varios}`;
const qs = (o: Record<string, string | null | undefined>): string =>
  new URLSearchParams(Object.entries(o).filter((e): e is [string, string] => e[1] !== null && e[1] !== undefined && e[1] !== '')).toString();
const primeroDeMes = (dia: string): string => `${dia.slice(0, 8)}01`;
const fechaHoraLocal = (v: string): string => `${fecha(v.slice(0, 10))} ${v.slice(11, 16)}`;
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
export const nombreMes = (p: string): string => `${MESES[Number(p.slice(5, 7)) - 1] ?? p} ${p.slice(0, 4)}`;
const enlaceKardex = (q: { idArticulo: string; idAlmacen?: string | null; idLote?: string | null; idSerie?: string | null; desde: string; hasta: string }): string =>
  `/inventario/kardex?${qs(q)}`;

function Avisos({ avisos }: { avisos: readonly string[] }): ReactNode {
  if (avisos.length === 0) return null;
  return <div className="nota aviso" role="status"><ul>{avisos.map((a) => <li key={a}>{a}</li>)}</ul></div>;
}
function Totales({ t }: { t: readonly { unidad: string; cantidad: string; filas: number }[] }): ReactNode {
  if (t.length === 0) return null;
  return <p className="pequena">Totales: {t.map((x) => `${n(x.cantidad)} ${x.unidad} (${plural(x.filas, 'fila', 'filas')})`).join(' · ')}</p>;
}

// ================================================================== stock histórico
/** F13.7: «Stock histórico» es la pestaña Stock con una fecha pasada. La URL antigua sigue funcionando. */
export function PaginaStockHistorico(): ReactNode {
  const [params] = useSearchParams();
  return <Navigate replace to={`/inventario/stock?${qs({ fecha: params.get('fecha') ?? hoyLocal() })}`} />;
}

/**
 * Stock al final de un día PASADO (hora de Madrid), del motor de stock histórico: lo pinta la pestaña Stock cuando la
 * fecha no es hoy. La fecha la gobierna esa página (`dia` / `fijarDia`); los filtros, este panel.
 */
export function StockAFecha({ dia, fijarDia }: { dia: string; fijarDia: (d: string) => void }): ReactNode {
  const opciones = useDatos(() => api.get<OpcionesHistorico>(`${H}/opciones`), []);
  const hoy = hoyLocal();
  const setDia = fijarDia;
  const [idCentro, setIdCentro] = useState('');
  const [almacen, setAlmacen] = useState<OpcionAlmacenStock | null>(null);
  const [articulo, setArticulo] = useState<OpcionArticuloStock | null>(null);
  const [lote, setLote] = useState<OpcionLote | null>(null);
  const [idFamilia, setIdFamilia] = useState('');
  const [pagina, setPagina] = useState(1);
  const consulta = qs({ fecha: dia, idCentro, idAlmacen: almacen?.id, idArticulo: articulo?.id, idLote: lote?.id, idFamilia, pagina: String(pagina), porPagina: '100' });
  const datos = useDatos(() => (dia === '' ? Promise.resolve(null) : api.get<StockHistorico>(`${H}/stock?${consulta}`)), [consulta]);
  const ir = (f: () => void): void => { f(); setPagina(1); };
  return (
      <div className="panel">
        <div className="panel-cuerpo form-rejilla">
          <div className="campo"><label htmlFor="sh-fecha">Fecha</label>
            <input id="sh-fecha" type="date" max={hoy} value={dia} onChange={(ev) => ir(() => setDia(ev.target.value))} /></div>
          <div className="campo"><label htmlFor="sh-centro">Centro</label>
            <select id="sh-centro" value={idCentro} onChange={(ev) => ir(() => setIdCentro(ev.target.value))}>
              <option value="">Todos</option>
              {(opciones.datos?.centros ?? []).map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.descripcion}</option>)}
            </select></div>
          <Buscador<OpcionAlmacenStock> etiqueta="Almacén" valor={almacen} alElegir={(a) => ir(() => setAlmacen(a))} buscar={buscarAlmacenes}
            clave={(a) => a.id} texto={nombreAlmacen} placeholder="Todos" />
          <Buscador<OpcionArticuloStock> etiqueta="Artículo" valor={articulo} alElegir={(a) => ir(() => { setArticulo(a); setLote(null); })}
            buscar={buscarArticulos(null)} clave={(a) => a.id} texto={nombreArticulo} placeholder="Todos" />
          {articulo !== null && articulo.trazabilidad?.controlaLotes === true && (
            <Buscador<OpcionLote> etiqueta="Lote" valor={lote} alElegir={(l) => ir(() => setLote(l))} buscar={buscarLotes({ idArticulo: articulo.id })}
              clave={(l) => l.id} texto={(l) => l.codigo} contexto={articulo.id} placeholder="Todos" />
          )}
          <div className="campo"><label htmlFor="sh-familia">Familia</label>
            <select id="sh-familia" value={idFamilia} onChange={(ev) => ir(() => setIdFamilia(ev.target.value))}>
              <option value="">Todas</option>
              {(opciones.datos?.familias ?? []).map((f) => <option key={f.id} value={f.id}>{f.codigo} · {f.descripcion}</option>)}
            </select></div>
        </div>
        <Panel estado={datos}>
          {(d) => (d === null ? <Vacio titulo="Elige una fecha" /> : (
            <div className="panel-cuerpo">
              <p className="fila"><strong>{d.corteTexto}</strong><span className="suave pequena">{plural(d.total, 'fila', 'filas')}</span></p>
              {d.filtrosTexto.length > 0 && <p className="pequena suave">Filtros: {d.filtrosTexto.map(([k, v]) => `${k}: ${v}`).join(' · ')}</p>}
              <Avisos avisos={d.avisos} />
              <Totales t={d.totales} />
              {d.filas.length === 0 ? <Vacio titulo="No había stock con estos filtros en esa fecha" /> : (
                <>
                  <div className="tabla-desliza">
                    <table aria-label="Stock histórico" className="tabla-tarjetas">
                      <thead><tr><th>Código</th><th>Artículo</th><th>Almacén</th><th>Centro</th><th>Lote</th><th>Serie</th><th>Unidad</th>
                        <th className="num">Stock</th><th><span className="sr-solo">Acciones</span></th></tr></thead>
                      <tbody>
                        {d.filas.map((f) => (
                          <tr key={`${f.idAlmacen}:${f.idArticulo}:${f.idLote ?? ''}:${f.idSerie ?? ''}:${f.unidad}`}>
                            <td className="celda-titulo mono">{f.codigoArticulo}</td>
                            <td data-label="Artículo">{f.descripcion}</td>
                            <td data-label="Almacén"><span className="mono">{f.codigoAlmacen}</span> <span className="suave">{f.almacen}</span></td>
                            <td data-label="Centro">{f.centroDesconocido ? <span className="etiqueta neutra" title="Sin evidencia del centro del almacén en esa fecha">Desconocido</span>
                              : f.centro === null ? <span className="suave">Sin centro</span> : f.centro.codigo}</td>
                            <td data-label="Lote">{f.lote ?? ''}{f.caducidad !== null && <div className="pequena suave">cad. {fecha(f.caducidad)}</div>}</td>
                            <td data-label="Serie">{f.serie ?? ''}</td>
                            <td data-label="Unidad">{f.unidad}</td>
                            <td data-label="Stock" className="num">{n(f.stock)}</td>
                            <td data-label="Kárdex"><Link to={enlaceKardex({ idArticulo: f.idArticulo, idAlmacen: f.idAlmacen, idLote: f.idLote, idSerie: f.idSerie,
                              desde: primeroDeMes(d.fecha), hasta: d.fecha })}>Ver kárdex</Link></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {d.total > d.porPagina && (
                    <nav className="fila" aria-label="Páginas">
                      <button type="button" className="pequeno" disabled={pagina === 1} onClick={() => setPagina(pagina - 1)}>Anterior</button>
                      <span className="suave pequena">Página {pagina} de {Math.ceil(d.total / d.porPagina)}</span>
                      <button type="button" className="pequeno" disabled={pagina * d.porPagina >= d.total} onClick={() => setPagina(pagina + 1)}>Siguiente</button>
                    </nav>
                  )}
                </>
              )}
            </div>
          ))}
        </Panel>
      </div>
  );
}

// ================================================================== kárdex
export function PaginaKardex(): ReactNode {
  const [params, setParams] = useSearchParams();
  const hoy = hoyLocal();
  const idArticulo = params.get('idArticulo') ?? '';
  const desde = params.get('desde') ?? primeroDeMes(hoy);
  const hasta = params.get('hasta') ?? hoy;
  const consulta = qs({ idArticulo, idAlmacen: params.get('idAlmacen'), idLote: params.get('idLote'), idSerie: params.get('idSerie'), desde, hasta });
  const datos = useDatos(() => (idArticulo === '' ? Promise.resolve(null) : api.get<Kardex>(`${H}/kardex?${consulta}`)), [consulta]);
  const [articulo, setArticulo] = useState<OpcionArticuloStock | null>(null);
  const [almacen, setAlmacen] = useState<OpcionAlmacenStock | null>(null);
  const poner = (c: Record<string, string | null>): void => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(c)) { if (v === null || v === '') p.delete(k); else p.set(k, v); }
    setParams(p);
  };
  return (
    <Shell dominio="movimientos" migas={[{ texto: 'Inventario' }, { texto: 'Movimientos', a: '/inventario/movimientos' }, { texto: 'Kárdex' }]} titulo="Kárdex"
      subtitulo="Movimientos en el orden en que ocurrieron, con el saldo antes y después de cada uno.">
      <div className="panel">
        <div className="panel-cuerpo form-rejilla">
          <Buscador<OpcionArticuloStock> etiqueta="Artículo" valor={articulo} alElegir={(a) => { setArticulo(a); poner({ idArticulo: a?.id ?? null, idLote: null, idSerie: null }); }}
            buscar={buscarArticulos(null)} clave={(a) => a.id} texto={nombreArticulo} placeholder={datos.datos === null ? 'Elige un artículo' : datos.datos.articulo.codigo} />
          <Buscador<OpcionAlmacenStock> etiqueta="Almacén" valor={almacen} alElegir={(a) => { setAlmacen(a); poner({ idAlmacen: a?.id ?? null }); }}
            buscar={buscarAlmacenes} clave={(a) => a.id} texto={nombreAlmacen} placeholder={params.get('idAlmacen') === null ? 'Todos' : 'El de la consulta'} />
          <div className="campo"><label htmlFor="kx-desde">Desde</label>
            <input id="kx-desde" type="date" max={hoy} value={desde} onChange={(ev) => poner({ desde: ev.target.value })} /></div>
          <div className="campo"><label htmlFor="kx-hasta">Hasta</label>
            <input id="kx-hasta" type="date" max={hoy} value={hasta} onChange={(ev) => poner({ hasta: ev.target.value })} /></div>
        </div>
        <Panel estado={datos}>
          {(k) => (k === null ? <Vacio titulo="Elige un artículo para ver su kárdex" /> : <TablaKardex k={k} />)}
        </Panel>
      </div>
    </Shell>
  );
}

function TablaKardex({ k }: { k: Kardex }): ReactNode {
  const u = k.articulo.unidad;
  return (
    <div className="panel-cuerpo">
      <div className="datos">
        <Dato e="Artículo" v={<><span className="mono">{k.articulo.codigo}</span> {k.articulo.descripcion}</>} />
        <Dato e="Ámbito" v={k.filtrosTexto.filter(([e]) => e !== 'Artículo').map(([e, v]) => `${e}: ${v}`).join(' · ') || 'Todos los almacenes de tu ámbito'} />
        <Dato e="Período" v={k.periodoTexto} />
        <Dato e="Saldo inicial" v={`${n(k.saldoInicial)} ${u}`} />
        <Dato e="Entradas / salidas" v={`+${n(k.entradas)} / −${n(k.salidas)} ${u}`} />
        <Dato e="Saldo final" v={<strong>{`${n(k.saldoFinal)} ${u}`}</strong>} />
      </div>
      {k.filas.length === 0 ? <Vacio titulo="Sin movimientos en el período" /> : (
        <div className="tabla-desliza">
          <table aria-label="Kárdex" className="tabla-tarjetas">
            <thead><tr><th>Fecha</th><th>Documento</th><th>Tipo</th><th className="num">Entrada</th><th className="num">Salida</th>
              <th className="num">Saldo</th><th>Lote</th><th>PT/OT</th><th>Usuario</th><th>Detalle</th></tr></thead>
            <tbody>
              {k.filas.map((f) => (
                <tr key={f.idMovimiento}>
                  <td className="celda-titulo">{fechaHoraLocal(f.fecha)}</td>
                  <td data-label="Documento"><span className="mono">{f.operacion}</span>{f.documento !== null && <div className="pequena">{f.documento}</div>}</td>
                  <td data-label="Tipo">{f.claseTexto}
                    {f.contraparte !== null && <div className="pequena suave">{f.clase === 'TRASLADO_SALIDA' ? `→ ${f.contraparte.codigo}` : `← ${f.contraparte.codigo}`}</div>}
                    {f.contraparteFueraDeAmbito && <div className="pequena suave">otro almacén fuera de tu ámbito</div>}
                    {f.rectificaA !== null && <div className="pequena suave">rectifica {f.rectificaA}</div>}</td>
                  <td data-label="Entrada" className="num">{n(f.entrada)}</td>
                  <td data-label="Salida" className="num">{n(f.salida)}</td>
                  <td data-label="Saldo" className="num"><strong>{n(f.saldo)}</strong></td>
                  <td data-label="Lote">{f.lote ?? ''}{f.serie !== null && <div className="pequena">{f.serie}</div>}</td>
                  <td data-label="PT/OT">{f.pt ?? f.ot ?? ''}</td>
                  <td data-label="Usuario">{f.usuario}</td>
                  <td data-label="Detalle">
                    <details>
                      <summary>Ver</summary>
                      <ul className="pequena">
                        <li>Almacén: {f.almacen.codigo} · {f.almacen.descripcion}</li>
                        <li>Saldo antes: {n(f.saldoAnterior)} {u} · movimiento {n(f.movimiento)} {u}</li>
                        {f.fechaDocumento !== null && <li>Fecha del documento: {fecha(f.fechaDocumento)}</li>}
                        <li>Centro: {f.centroDesconocido ? 'desconocido (histórico sin evidencia)' : f.centro === null ? 'sin centro' : `${f.centro.codigo} · ${f.centro.descripcion}`}</li>
                        {f.exportaciones.length > 0 && <li>Exportado: {f.exportaciones.join(', ')}</li>}
                      </ul>
                    </details>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ================================================================== cierres (listado)
function EstadoCierre({ c }: { c: Pick<CierreResumen, 'estado' | 'afectado'> }): ReactNode {
  if (c.estado === 'SUSTITUIDO') return <span className="etiqueta neutra">Sustituido</span>;
  return c.afectado === true ? <span className="etiqueta aviso" title="Hay movimientos posteriores que afectan al período">Vigente · afectado</span>
    : <span className="etiqueta exito">Vigente</span>;
}

export function PaginaCierres(): ReactNode {
  const { puede } = useSesion();
  const [periodo, setPeriodo] = useState('');
  const [estado, setEstado] = useState('');
  const [pagina, setPagina] = useState(1);
  const [nuevo, setNuevo] = useState(false);
  const lista = useDatos(() => api.get<{ filas: CierreResumen[]; hayMas: boolean }>(`${C}?${qs({ periodo, estado, pagina: String(pagina), porPagina: '25' })}`),
    [periodo, estado, pagina]);
  return (
    <Shell dominio="movimientos" migas={[{ texto: 'Inventario' }, { texto: 'Movimientos', a: '/inventario/movimientos' }, { texto: 'Cierres' }]} titulo="Cierres de inventario"
      subtitulo="Foto mensual del stock (inicial, movimientos y final). Es informativa: no bloquea operaciones ni mueve stock."
      acciones={puede('cierres_inventario:crear') ? <button type="button" className="principal" onClick={() => setNuevo(true)}>Nuevo cierre</button> : undefined}>
      <div className="panel">
        <div className="panel-cuerpo form-rejilla">
          <div className="campo"><label htmlFor="cie-f-periodo">Mes</label>
            <input id="cie-f-periodo" type="month" value={periodo} onChange={(ev) => { setPeriodo(ev.target.value); setPagina(1); }} /></div>
          <div className="campo"><label htmlFor="cie-f-estado">Estado</label>
            <select id="cie-f-estado" value={estado} onChange={(ev) => { setEstado(ev.target.value); setPagina(1); }}>
              <option value="">Todos</option><option value="VIGENTE">Vigentes</option><option value="SUSTITUIDO">Sustituidos</option>
            </select></div>
        </div>
        <Panel estado={lista}>
          {(d) => (d.filas.length === 0 ? <Vacio titulo="Todavía no hay cierres" /> : (
            <>
              <div className="tabla-desliza">
                <table aria-label="Cierres" className="tabla-tarjetas">
                  <thead><tr><th>Período</th><th>Ámbito</th><th>Versión</th><th>Generado</th><th>Usuario</th><th>Estado</th><th>Incidencias</th><th>Conciliación</th></tr></thead>
                  <tbody>
                    {d.filas.map((c) => (
                      <tr key={c.id}>
                        <td className="celda-titulo"><Link to={`/inventario/cierres/${c.id}`}>{nombreMes(c.periodo)}</Link>
                          <div className="pequena suave mono">{c.codigo}</div></td>
                        <td data-label="Ámbito">{c.ambitoTexto}</td>
                        <td data-label="Versión">v{c.version}</td>
                        <td data-label="Generado">{fechaHora(c.creadoEn)}</td>
                        <td data-label="Usuario">{c.creadoPor}</td>
                        <td data-label="Estado"><EstadoCierre c={c} /></td>
                        <td data-label="Incidencias">{c.numIncidencias === 0 ? <span className="suave">Ninguna</span> : c.numIncidencias}</td>
                        <td data-label="Conciliación">{c.conciliacion === null ? <span className="suave">Sin conciliar</span>
                          : c.conciliacion.diferencias + c.conciliacion.sinCorrespondencia + c.conciliacion.soloNeatos === 0
                            ? <span className="etiqueta exito">Cuadra</span>
                            : <span className="etiqueta aviso">{plural(c.conciliacion.diferencias, 'diferencia', 'diferencias')}</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <nav className="fila" aria-label="Páginas">
                <button type="button" className="pequeno" disabled={pagina === 1} onClick={() => setPagina(pagina - 1)}>Anterior</button>
                <span className="suave pequena">Página {pagina}</span>
                <button type="button" className="pequeno" disabled={!d.hayMas} onClick={() => setPagina(pagina + 1)}>Siguiente</button>
              </nav>
            </>
          ))}
        </Panel>
      </div>
      {nuevo && <DialogoNuevoCierre cerrar={() => setNuevo(false)} />}
    </Shell>
  );
}

function DialogoNuevoCierre({ cerrar }: { cerrar: () => void }): ReactNode {
  const navegar = useNavigate();
  const opciones = useDatos(() => api.get<OpcionesHistorico>(`${H}/opciones`), []);
  const hoy = hoyLocal();
  const anterior = hoyLocal(0, new Date(Number(hoy.slice(0, 4)), Number(hoy.slice(5, 7)) - 2, 1)).slice(0, 7);
  const [periodo, setPeriodo] = useState(anterior);
  const [idCentro, setIdCentro] = useState('');
  const [almacen, setAlmacen] = useState<OpcionAlmacenStock | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const campos = useMemo(() => erroresDe(error), [error]);
  async function generar(): Promise<void> {
    setError(null);
    setOcupado(true);
    try {
      const filtros = almacen !== null ? { idAlmacen: almacen.id } : idCentro !== '' ? { idCentro } : {};
      const c = await api.post<CierreDetalle>(C, { periodo, filtros });
      navegar(`/inventario/cierres/${c.id}`);
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  }
  return (
    <Modal titulo="Nuevo cierre" cerrar={cerrar}
      pie={<><button type="button" onClick={cerrar}>Cancelar</button>
        <button type="button" className="principal" disabled={ocupado || periodo === ''} onClick={() => void generar()}>{ocupado ? 'Generando…' : 'Generar cierre'}</button></>}>
      <p>Guarda la foto del mes: stock al empezar, movimientos y stock al terminar (hora de Madrid). No bloquea nada; si después cambia algo del mes, se avisa y se puede regenerar.</p>
      <div className="form-rejilla">
        <div className="campo"><label htmlFor="cie-periodo">Mes</label>
          <input id="cie-periodo" type="month" max={hoy.slice(0, 7)} value={periodo} onChange={(ev) => setPeriodo(ev.target.value)} />
          {campos['periodo'] !== undefined && <span className="error-campo">{campos['periodo']}</span>}</div>
        <div className="campo"><label htmlFor="cie-centro">Centro</label>
          <select id="cie-centro" value={idCentro} disabled={almacen !== null} onChange={(ev) => setIdCentro(ev.target.value)}>
            <option value="">Todos los de mi ámbito</option>
            {(opciones.datos?.centros ?? []).map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.descripcion}</option>)}
          </select></div>
        <Buscador<OpcionAlmacenStock> etiqueta="Almacén" valor={almacen} alElegir={setAlmacen} buscar={buscarAlmacenes}
          clave={(a) => a.id} texto={nombreAlmacen} placeholder="Todos" />
      </div>
      {error !== null && Object.keys(campos).length === 0 && <ErrorCaja error={error} />}
    </Modal>
  );
}

// ================================================================== cierre (ficha)
function Dato({ e, v }: { e: string; v: ReactNode }): ReactNode {
  return <div className="dato"><div className="etiqueta-dato">{e}</div><div className="valor-dato">{v ?? '—'}</div></div>;
}
const ETIQUETA_INCIDENCIA: Readonly<Record<string, string>> = {
  DOCUMENTO_TARDIO: 'Documento del período registrado después', DOCUMENTO_ANTERIOR: 'Documento de otro período registrado en éste',
  CENTRO_DESCONOCIDO: 'Centro desconocido', PERIODO_EN_CURSO: 'Período en curso', DESCUADRE_PROYECCION: 'Alerta técnica (saldos)', CLASE_OTROS: 'Clasificación',
};
const neto = (c: Record<string, { entradas: string; salidas: string }>, claves: readonly string[], salida: boolean): number =>
  claves.reduce((s, k) => s + (c[k] === undefined ? 0 : (salida ? Number(c[k].salidas) - Number(c[k].entradas) : Number(c[k].entradas) - Number(c[k].salidas))), 0);
const num = (v: number): string => n(String(Math.round(v * 1e6) / 1e6));

export function PaginaCierre(): ReactNode {
  const { id = '' } = useParams();
  const { puede } = useSesion();
  const navegar = useNavigate();
  const d = useDatos(() => api.get<CierreDetalle>(`${C}/${id}`), [id]);
  const [texto, setTexto] = useState('');
  const [pagina, setPagina] = useState(1);
  const lineas = useDatos(() => api.get<{ filas: LineaCierre[]; total: number }>(`${C}/${id}/lineas?${qs({ texto, pagina: String(pagina), porPagina: '50' })}`),
    [id, texto, pagina]);
  const [regenerar, setRegenerar] = useState(false);
  const [conciliar, setConciliar] = useState(false);
  const titulo = d.datos === null ? 'Cierre' : `${d.datos.codigo} v${String(d.datos.version)}`;
  return (
    <Shell dominio="movimientos" migas={[{ texto: 'Inventario' }, { texto: 'Movimientos', a: '/inventario/movimientos' }, { texto: 'Cierres', a: '/inventario/cierres' }, { texto: titulo }]} titulo={titulo}
      subtitulo={d.datos === null ? undefined : `${nombreMes(d.datos.periodo)} · ${d.datos.ambitoTexto}`}>
      <Panel estado={d}>
        {(x) => {
          const vigente = x.estado === 'VIGENTE';
          return (
            <>
              <div className="panel"><div className="panel-cuerpo">
                <p><EstadoCierre c={x} /></p>
                <div className="barra-acciones" role="group" aria-label="Acciones del cierre">
                  {vigente && puede('exportaciones_inventario:crear') && (
                    <button type="button" onClick={() => navegar(`/inventario/exportaciones/nueva?cierre=${x.id}`)}>Exportar stock a fecha</button>
                  )}
                  {vigente && puede('cierres_inventario:conciliar') && <button type="button" onClick={() => setConciliar(true)}>Conciliar con el ERP</button>}
                  {vigente && puede('cierres_inventario:regenerar') && <button type="button" onClick={() => setRegenerar(true)}>Regenerar</button>}
                </div>
                {x.situacion?.afectado === true && (
                  <div className="nota aviso" role="alert">
                    <strong>Existen movimientos posteriores que afectan al período.</strong> Esta foto no cambia; regenera para obtener una versión nueva.
                    <ul>
                      {x.situacion.movimientosNuevos > 0 && <li>{plural(x.situacion.movimientosNuevos, 'movimiento con fecha del período que no estaba', 'movimientos con fecha del período que no estaban')} al generar
                        {x.situacion.nuevosEnPeriodo.length > 0 && <> ({x.situacion.nuevosEnPeriodo.map((m) => m.operacion).join(', ')})</>}.</li>}
                      {x.situacion.documentosTardiosNuevos.map((m) => (
                        <li key={m.idMovimiento}>{m.operacion}: documento del {fecha(m.fechaDocumento)} registrado el {fechaHora(m.fechaMovimiento)} (no cambia el stock físico del período).</li>
                      ))}
                    </ul>
                  </div>
                )}
                {x.sustituidoPor !== null && (
                  <p className="nota info" role="note">Sustituido por la <Link to={`/inventario/cierres/${x.sustituidoPor.id}`}>versión {x.sustituidoPor.version}</Link>. Se conserva tal como se generó.</p>
                )}
                <div className="datos">
                  <Dato e="Período" v={`${fecha(x.desde)} – ${fecha(x.hasta)} (Europe/Madrid)`} />
                  <Dato e="Ámbito" v={`${x.ambitoTexto}: ${x.ambito.almacenes.map((a) => a.codigo).join(', ')}`} />
                  <Dato e="Generado" v={`${fechaHora(x.creadoEn)} · ${x.creadoPor}`} />
                  {x.motivo !== null && <Dato e="Motivo de la versión" v={x.motivo} />}
                  <Dato e="Referencias" v={x.kpi.referencias} />
                  <Dato e="Movimientos del período" v={x.kpi.movimientos} />
                  <Dato e="Documentos tardíos" v={x.kpi.documentosTardios} />
                  <Dato e="Almacenes con centro desconocido" v={x.kpi.centrosDesconocidos} />
                  {x.conciliacion !== null && <Dato e="Conciliación" v={<Link to={`/inventario/cierres/${x.id}/conciliaciones/${String(x.conciliacion.numero)}`}>
                    {plural(x.conciliacion.diferencias, 'diferencia', 'diferencias')} · {plural(x.conciliacion.sinCorrespondencia + x.conciliacion.soloNeatos, 'fila sin correspondencia', 'filas sin correspondencia')}</Link>} />}
                </div>
                {x.kpi.periodoEnCurso && <p className="nota info" role="note">El mes todavía no ha terminado: el stock final es el de este momento.</p>}
              </div></div>

              <div className="panel"><div className="panel-cuerpo">
                <h2 className="titulo-seccion">Resumen por unidad</h2>
                {x.kpi.porUnidad.length === 0 ? <Vacio titulo="Sin stock ni movimientos en el período" /> : (
                  <div className="tabla-desliza">
                    <table aria-label="Resumen por unidad" className="tabla-tarjetas">
                      <thead><tr><th>Unidad</th><th className="num">Referencias</th><th className="num">Stock inicial</th><th className="num">Entradas</th>
                        <th className="num">Salidas</th><th className="num">Consumos</th><th className="num">Ajustes</th><th className="num">Stock final</th></tr></thead>
                      <tbody>
                        {x.kpi.porUnidad.map((u) => (
                          <tr key={u.unidad}>
                            <td className="celda-titulo">{u.unidad}</td>
                            <td data-label="Referencias" className="num">{u.referencias}</td>
                            <td data-label="Stock inicial" className="num">{n(u.stockInicial)}</td>
                            <td data-label="Entradas" className="num">{n(u.entradas)}</td>
                            <td data-label="Salidas" className="num">{n(u.salidas)}</td>
                            <td data-label="Consumos" className="num">{num(neto(u.porClase, ['CONSUMO_PT', 'CONSUMO_OT'], true))}</td>
                            <td data-label="Ajustes" className="num">{num(neto(u.porClase, ['AJUSTE_POSITIVO', 'AJUSTE_NEGATIVO'], false))}</td>
                            <td data-label="Stock final" className="num"><strong>{n(u.stockFinal)}</strong></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="pequena suave">Cada unidad por separado: nunca se suman kilos con unidades. Inicial + entradas − salidas = final, línea a línea.</p>
              </div></div>

              {x.incidenciasDetalle.length > 0 && (
                <div className="panel"><div className="panel-cuerpo">
                  <h2 className="titulo-seccion">Incidencias ({x.incidenciasDetalle.length})</h2>
                  <ul>{x.incidenciasDetalle.map((i, k) => <li key={k}><span className="etiqueta neutra">{ETIQUETA_INCIDENCIA[i.tipo] ?? i.tipo}</span> {i.mensaje}</li>)}</ul>
                </div></div>
              )}

              <div className="panel"><div className="panel-cuerpo">
                <h2 className="titulo-seccion">Líneas</h2>
                <div className="form-rejilla">
                  <div className="campo"><label htmlFor="cie-buscar">Buscar</label>
                    <input id="cie-buscar" type="search" value={texto} placeholder="Artículo, almacén, lote o serie" onChange={(ev) => { setTexto(ev.target.value); setPagina(1); }} /></div>
                </div>
                <Panel estado={lineas}>
                  {(l) => (l.filas.length === 0 ? <Vacio titulo="Sin líneas" /> : (
                    <>
                      <div className="tabla-desliza">
                        <table aria-label="Líneas del cierre" className="tabla-tarjetas">
                          <thead><tr><th>Artículo</th><th>Almacén</th><th>Lote / serie</th><th>Unidad</th><th className="num">Inicial</th>
                            <th className="num">Entradas</th><th className="num">Salidas</th><th className="num">Final</th><th><span className="sr-solo">Acciones</span></th></tr></thead>
                          <tbody>
                            {l.filas.map((f) => (
                              <tr key={f.orden}>
                                <td className="celda-titulo"><span className="mono">{f.codigoArticulo}</span> <span className="suave">{f.descripcion}</span></td>
                                <td data-label="Almacén"><span className="mono">{f.codigoAlmacen}</span>{f.centroDesconocido && <div className="pequena suave">centro desconocido</div>}</td>
                                <td data-label="Lote / serie">{[f.lote, f.serie].filter((v) => v !== null).join(' · ')}</td>
                                <td data-label="Unidad">{f.unidad}</td>
                                <td data-label="Inicial" className="num">{n(f.stockInicial)}</td>
                                <td data-label="Entradas" className="num" title={f.desglose.filter((q) => Number(q.entradas) > 0).map((q) => `${q.texto}: ${n(q.entradas)}`).join(' · ')}>{n(f.entradas)}</td>
                                <td data-label="Salidas" className="num" title={f.desglose.filter((q) => Number(q.salidas) > 0).map((q) => `${q.texto}: ${n(q.salidas)}`).join(' · ')}>{n(f.salidas)}</td>
                                <td data-label="Final" className="num"><strong>{n(f.stockFinal)}</strong></td>
                                <td data-label="Kárdex"><Link to={enlaceKardex({ idArticulo: f.idArticulo, idAlmacen: f.idAlmacen, idLote: f.idLote, idSerie: f.idSerie,
                                  desde: x.desde, hasta: x.hasta })}>Kárdex</Link></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {l.total > 50 && (
                        <nav className="fila" aria-label="Páginas de líneas">
                          <button type="button" className="pequeno" disabled={pagina === 1} onClick={() => setPagina(pagina - 1)}>Anterior</button>
                          <span className="suave pequena">Página {pagina} de {Math.ceil(l.total / 50)}</span>
                          <button type="button" className="pequeno" disabled={pagina * 50 >= l.total} onClick={() => setPagina(pagina + 1)}>Siguiente</button>
                        </nav>
                      )}
                    </>
                  ))}
                </Panel>
              </div></div>

              <div className="panel"><div className="panel-cuerpo">
                <h2 className="titulo-seccion">Versiones e historial</h2>
                <ul>{x.versiones.map((v) => (
                  <li key={v.id}>{v.id === x.id ? <strong>v{v.version}</strong> : <Link to={`/inventario/cierres/${v.id}`}>v{v.version}</Link>}
                    {' '}· {v.estado === 'VIGENTE' ? 'vigente' : 'sustituida'} · {fechaHora(v.creadoEn)} · {v.creadoPor}{v.motivo !== null && <> · «{v.motivo}»</>}</li>
                ))}</ul>
                {x.conciliaciones.length > 0 && (
                  <p>Conciliaciones: {x.conciliaciones.map((k, i) => <span key={k.id}>{i > 0 && ', '}<Link to={`/inventario/cierres/${x.id}/conciliaciones/${String(k.numero)}`}>
                    nº {k.numero}</Link> <span className="suave pequena">({k.sistema}, {fechaHora(k.creadoEn)})</span></span>)}</p>
                )}
                {x.exportaciones.length > 0 && (
                  <p>Exportaciones: {x.exportaciones.map((e, i) => <span key={e.id}>{i > 0 && ', '}<Link className="mono" to={`/inventario/exportaciones/${e.id}`}>{e.numero}</Link></span>)}</p>
                )}
                <details className="detalles-tecnicos">
                  <summary>Historial del cierre</summary>
                  <ul className="pequena">{x.eventos.map((e, i) => <li key={i}>{fechaHora(e.en)} · {e.tipo.toLowerCase()} · {e.usuario}</li>)}</ul>
                </details>
              </div></div>
              {regenerar && <DialogoRegenerar c={x} cerrar={() => setRegenerar(false)} alRegenerar={(nuevo) => { setRegenerar(false); navegar(`/inventario/cierres/${nuevo}`); }} />}
              {conciliar && <DialogoConciliar c={x} cerrar={() => setConciliar(false)}
                alConciliar={(numero) => { setConciliar(false); navegar(`/inventario/cierres/${x.id}/conciliaciones/${String(numero)}`); }} />}
            </>
          );
        }}
      </Panel>
    </Shell>
  );
}

function DialogoRegenerar({ c, cerrar, alRegenerar }: { c: CierreDetalle; cerrar: () => void; alRegenerar: (id: string) => void }): ReactNode {
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const campos = useMemo(() => erroresDe(error), [error]);
  async function confirmar(): Promise<void> {
    setError(null);
    setOcupado(true);
    try {
      alRegenerar((await api.post<CierreDetalle>(`${C}/${c.id}/regenerar`, { motivo })).id);
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  }
  return (
    <Modal titulo={`Regenerar ${c.codigo}`} cerrar={cerrar}
      pie={<><button type="button" onClick={cerrar}>Cancelar</button>
        <button type="button" className="principal" disabled={ocupado || motivo.trim().length < 5} onClick={() => void confirmar()}>{ocupado ? 'Regenerando…' : `Generar v${String(c.version + 1)}`}</button></>}>
      <p>Se crea la versión {c.version + 1} con lo que dice hoy el ledger. La versión {c.version} no se borra ni cambia: queda sustituida.</p>
      <div className="campo"><label htmlFor="cie-motivo">Motivo</label>
        <textarea id="cie-motivo" rows={2} maxLength={500} value={motivo} onChange={(ev) => setMotivo(ev.target.value)} />
        {campos['motivo'] !== undefined && <span className="error-campo">{campos['motivo']}</span>}</div>
      {error !== null && Object.keys(campos).length === 0 && <ErrorCaja error={error} />}
    </Modal>
  );
}

interface FilaManual { codigoArticulo: string; codigoAlmacen: string; lote: string; stockErp: string }
const filaVacia = (): FilaManual => ({ codigoArticulo: '', codigoAlmacen: '', lote: '', stockErp: '' });

function DialogoConciliar({ c, cerrar, alConciliar }: { c: CierreDetalle; cerrar: () => void; alConciliar: (numero: number) => void }): ReactNode {
  const opciones = useDatos(() => api.get<OpcionesHistorico>(`${H}/opciones`), []);
  const [modo, setModo] = useState<'FICHERO' | 'MANUAL'>('FICHERO');
  const [idSistema, setIdSistema] = useState('');
  const [fichero, setFichero] = useState<File | null>(null);
  const [filas, setFilas] = useState<FilaManual[]>([filaVacia()]);
  const [error, setError] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const campos = useMemo(() => erroresDe(error), [error]);
  async function plantilla(formato: 'XLSX' | 'CSV'): Promise<void> {
    setError(null);
    try {
      const f = await api.fichero(`${C}/${c.id}/plantilla?formato=${formato}`);
      guardarFichero(f.blob, f.nombre);
    } catch (e) {
      setError(e);
    }
  }
  async function enviar(): Promise<void> {
    setError(null);
    setOcupado(true);
    try {
      let r: Conciliacion;
      if (modo === 'FICHERO') {
        const datos = new FormData();
        if (idSistema !== '') datos.append('idSistema', idSistema);
        if (fichero !== null) datos.append('fichero', fichero);
        r = await api.subir<Conciliacion>(`${C}/${c.id}/conciliaciones`, datos);
      } else {
        const lineas = filas.filter((f) => f.codigoArticulo.trim() !== '' || f.stockErp.trim() !== '')
          .map((f) => ({ codigoArticulo: f.codigoArticulo, codigoAlmacen: f.codigoAlmacen, lote: f.lote.trim() === '' ? null : f.lote, stockErp: f.stockErp }));
        r = await api.post<Conciliacion>(`${C}/${c.id}/conciliaciones`, { ...(idSistema === '' ? {} : { idSistema }), lineas });
      }
      alConciliar(r.numero);
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  }
  const cambiarFila = (i: number, cambio: Partial<FilaManual>): void => setFilas(filas.map((f, k) => (k === i ? { ...f, ...cambio } : f)));
  return (
    <Modal titulo={`Conciliar ${c.codigo} v${String(c.version)}`} cerrar={cerrar}
      pie={<><button type="button" onClick={cerrar}>Cancelar</button>
        <button type="button" className="principal" disabled={ocupado || (modo === 'FICHERO' && fichero === null)} onClick={() => void enviar()}>{ocupado ? 'Comparando…' : 'Comparar'}</button></>}>
      <p>Compara el stock final del cierre con el que declara el ERP. <strong>No ajusta nada:</strong> si hay diferencias, se investigan o se corrigen con un inventario o un ajuste normal.</p>
      <div className="form-rejilla">
        <div className="campo"><label htmlFor="conc-modo">Cómo</label>
          <select id="conc-modo" value={modo} onChange={(ev) => setModo(ev.target.value as 'FICHERO' | 'MANUAL')}>
            <option value="FICHERO">Subir fichero (CSV o Excel)</option><option value="MANUAL">Escribir las líneas</option>
          </select></div>
        <div className="campo"><label htmlFor="conc-sistema">Códigos de</label>
          <select id="conc-sistema" value={idSistema} onChange={(ev) => setIdSistema(ev.target.value)}>
            <option value="">NeatOS (código de negocio)</option>
            {(opciones.datos?.sistemas ?? []).map((s) => <option key={s.id} value={s.id}>{s.codigo} · {s.descripcion}</option>)}
          </select></div>
      </div>
      {modo === 'FICHERO' ? (
        <>
          <p className="fila pequena">Plantilla del cierre (con los artículos y almacenes; rellena <span className="mono">stock_erp</span>):
            <button type="button" className="pequeno" onClick={() => void plantilla('XLSX')}>Excel</button>
            <button type="button" className="pequeno" onClick={() => void plantilla('CSV')}>CSV</button></p>
          <div className="campo"><label htmlFor="conc-fichero">Fichero</label>
            <input id="conc-fichero" type="file" accept=".csv,.txt,.xlsx" onChange={(ev) => setFichero(ev.target.files?.[0] ?? null)} />
            {campos['fichero'] !== undefined && <span className="error-campo">{campos['fichero']}</span>}</div>
        </>
      ) : (
        <div className="tabla-desliza">
          <table aria-label="Stock declarado por el ERP" className="tabla-compacta">
            <thead><tr><th>Artículo</th><th>Almacén</th><th>Lote</th><th>Stock ERP</th></tr></thead>
            <tbody>
              {filas.map((f, i) => (
                <tr key={i}>
                  <td><input aria-label={`Artículo, línea ${String(i + 1)}`} value={f.codigoArticulo} onChange={(ev) => cambiarFila(i, { codigoArticulo: ev.target.value })} /></td>
                  <td><input aria-label={`Almacén, línea ${String(i + 1)}`} value={f.codigoAlmacen} onChange={(ev) => cambiarFila(i, { codigoAlmacen: ev.target.value })} /></td>
                  <td><input aria-label={`Lote, línea ${String(i + 1)}`} value={f.lote} onChange={(ev) => cambiarFila(i, { lote: ev.target.value })} /></td>
                  <td><input aria-label={`Stock ERP, línea ${String(i + 1)}`} inputMode="decimal" value={f.stockErp} onChange={(ev) => cambiarFila(i, { stockErp: ev.target.value })} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className="pequeno" onClick={() => setFilas([...filas, filaVacia()])}>Añadir línea</button>
        </div>
      )}
      {error !== null && <ErrorCaja error={error} />}
    </Modal>
  );
}

// ================================================================== conciliación
const ESTADO_CONCILIACION: Readonly<Record<EstadoConciliacion, [string, string]>> = {
  CUADRA: ['Cuadra', 'exito'], DIFERENCIA: ['Diferencia', 'aviso'], SIN_CORRESPONDENCIA: ['Sin correspondencia', 'peligro'], SOLO_NEATOS: ['Sólo en NeatOS', 'neutra'],
};

export function PaginaConciliacion(): ReactNode {
  const { id = '', numero = '' } = useParams();
  const d = useDatos(() => api.get<Conciliacion>(`${C}/${id}/conciliaciones/${numero}`), [id, numero]);
  const titulo = d.datos === null ? 'Conciliación' : `Conciliación nº ${String(d.datos.numero)}`;
  return (
    <Shell dominio="movimientos" migas={[{ texto: 'Inventario' }, { texto: 'Cierres', a: '/inventario/cierres' },
      { texto: d.datos === null ? 'Cierre' : `${d.datos.cierre.codigo} v${String(d.datos.cierre.version)}`, a: `/inventario/cierres/${id}` }, { texto: titulo }]}
      titulo={titulo} subtitulo="NeatOS frente a lo que declara el ERP. Diferencia = ERP − NeatOS. No se ha ajustado nada.">
      <Panel estado={d}>
        {(k) => (
          <div className="panel"><div className="panel-cuerpo">
            <div className="datos">
              <Dato e="Cierre" v={<Link to={`/inventario/cierres/${k.cierre.id}`}>{k.cierre.codigo} v{k.cierre.version}</Link>} />
              <Dato e="Códigos" v={k.sistema === 'NEATOS_ESTANDAR' ? 'NeatOS (código de negocio)' : k.sistema} />
              <Dato e="Origen" v={k.origen === 'FICHERO' ? `Fichero ${k.nombreFichero ?? ''}` : 'Escrito a mano'} />
              <Dato e="Hecha" v={`${fechaHora(k.creadoEn)} · ${k.creadoPor}`} />
              <Dato e="Resultado" v={`${plural(k.resumen.cuadran ?? 0, 'cuadra', 'cuadran')} · ${plural(k.resumen.diferencias ?? 0, 'diferencia', 'diferencias')} · ${plural(
                (k.resumen.sinCorrespondencia ?? 0) + (k.resumen.soloNeatos ?? 0), 'sin correspondencia', 'sin correspondencia')}`} />
              {(k.resumen.porUnidad ?? []).length > 0 && <Dato e="Diferencia por unidad" v={(k.resumen.porUnidad ?? []).map((u) => `${num(u.diferencia)} ${u.unidad}`).join(' · ')} />}
            </div>
            {k.filasOmitidas !== undefined && k.filasOmitidas > 0 && <p className="pequena suave">{plural(k.filasOmitidas, 'fila del fichero sin stock no se ha declarado', 'filas del fichero sin stock no se han declarado')}.</p>}
            <div className="tabla-desliza">
              <table aria-label="Líneas de la conciliación" className="tabla-tarjetas">
                <thead><tr><th>Estado</th><th>Artículo</th><th>Almacén</th><th>Lote</th><th className="num">NeatOS</th><th className="num">ERP</th>
                  <th className="num">Diferencia</th><th>Detalle</th></tr></thead>
                <tbody>
                  {k.lineas.map((l) => (
                    <tr key={l.orden}>
                      <td className="celda-titulo"><span className={`etiqueta ${ESTADO_CONCILIACION[l.estado][1]}`}>{ESTADO_CONCILIACION[l.estado][0]}</span></td>
                      <td data-label="Artículo" className="mono">{l.codigoArticulo ?? ''}</td>
                      <td data-label="Almacén" className="mono">{l.codigoAlmacen ?? ''}</td>
                      <td data-label="Lote">{l.lote ?? ''}</td>
                      <td data-label="NeatOS" className="num">{n(l.stockNeatos)}</td>
                      <td data-label="ERP" className="num">{n(l.stockErp)}</td>
                      <td data-label="Diferencia" className="num">{l.diferencia === null ? '' : <strong>{n(l.diferencia)} {l.unidad ?? ''}</strong>}</td>
                      <td data-label="Detalle" className="pequena">{l.mensaje}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div></div>
        )}
      </Panel>
    </Shell>
  );
}
